class ProductCompare extends HTMLElement {
    constructor() {
        super();
        
        // Initialize properties with error handling
        this.initializeElements();
        this.initializeEventListeners();
        this.checkProductsInSessionStorage();
        this.checkURLOnload();
        this.initializeUI();
        this.boundHandleProductClick = this.handleProductClick.bind(this)
    }

    initializeElements() {
        // Safe element selection with error handling
        this.buttonWrapper = this.querySelector('.compare__button__container');
        this.compareButton = this.querySelector('.compare__button__container button');
        this.compareModalButton = this.querySelector('.compare-modal__button');
        this.closeButton = this.querySelector('.close__compare');
        
        // Validate required elements
        if (!this.buttonWrapper || !this.compareButton || !this.compareModalButton || !this.closeButton) {
            console.error('ProductCompare: Required elements not found');
            return;
        }
    }

    initializeEventListeners() {
        // Add event listeners with error handling
        try {
            if (this.compareButton) {
                this.compareButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.openViewer();
                });
            }
            
            if (this.compareModalButton) {
                this.compareModalButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.openCompareModal();
                });
            }
            
            if (this.closeButton) {
                this.closeButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.closeViewer();
                });
            }
        } catch (error) {
            console.error('ProductCompare: Error initializing event listeners:', error);
        }
    }

    initializeUI() {
        // Initialize UI state
        const productsContainer = this.buttonWrapper?.querySelector('.selected-products');
        if (productsContainer) {
            productsContainer.style.display = 'none';
        }
    }

    checkURLOnload() {
        try {
            const currentURL = window.location.href;
            const previousURL = sessionStorage.getItem('previousURL');

            // Check if the URL has changed
            if (previousURL && previousURL !== currentURL) {
                // Clear comparison data and reset all product overlays
                this.clearComparisonData();
                console.log("compareProducts removed from sessionStorage due to URL change.");
            }

            // Update the previous URL in sessionStorage
            sessionStorage.setItem('previousURL', currentURL);
        } catch (error) {
            console.error('ProductCompare: Error checking URL:', error);
        }
    }

    clearComparisonData() {
        try {
            // Remove from sessionStorage
            sessionStorage.removeItem('compareProducts');
            
            // Reset all product card overlays
            this.resetAllProductOverlays();
            
            // Update UI state
            this.updateCompareButtonState();
        } catch (error) {
            console.error('ProductCompare: Error clearing comparison data:', error);
        }
    }

    resetAllProductOverlays() {
        try {
            const productCards = document.querySelectorAll('[data-product-variant-handle]');
            productCards.forEach(card => {
                this.removeProductOverlay(card);
            });
        } catch (error) {
            console.error('ProductCompare: Error resetting product overlays:', error);
        }
    }

    checkProductsInSessionStorage() {
        try {
            const compareProducts = this.getCompareProducts();
            
            if (compareProducts.length > 0) {
                // For each product in comparison, find its card and set properties
                compareProducts.forEach(product => {
                    const productCard = document.querySelector(`[data-product-variant-handle="${product.handle}"]`);
                    if (productCard) {
                        this.applyProductOverlay(productCard);
                    }
                });
                
                // Update the compare button state
                this.updateCompareButtonState();
            }
        } catch (error) {
            console.error('ProductCompare: Error checking products in session storage:', error);
        }
    }

    getCompareProducts() {
        try {
            return JSON.parse(sessionStorage.getItem('compareProducts') || '[]');
        } catch (error) {
            console.error('ProductCompare: Error parsing compare products from sessionStorage:', error);
            return [];
        }
    }

    setCompareProducts(products) {
        try {
            sessionStorage.setItem('compareProducts', JSON.stringify(products));
        } catch (error) {
            console.error('ProductCompare: Error saving compare products to sessionStorage:', error);
        }
    }

    applyProductOverlay(productCard) {
        if (!productCard) return;
        
        try {
            productCard.style.setProperty('--overlay-background', 'transparent');
            productCard.style.setProperty('--overlay-opacity', '1');
            productCard.classList.add('product-selected');
        } catch (error) {
            console.error('ProductCompare: Error applying product overlay:', error);
        }
    }

    removeProductOverlay(productCard) {
        if (!productCard) return;
        
        try {
            productCard.style.setProperty('--overlay-background', '#fff');
            productCard.style.setProperty('--overlay-opacity', '0.6');
            productCard.classList.remove('product-selected');
        } catch (error) {
            console.error('ProductCompare: Error removing product overlay:', error);
        }
    }

    openViewer() {
        
        try {
            if (!this.buttonWrapper) return;
            
            this.buttonWrapper.classList.add('active');
            
            const closeButton = this.buttonWrapper.querySelector('.close__compare');
            if (closeButton) {
                closeButton.classList.add('active');
            }
            
            // Hide compare button and show modal button
            if (this.compareButton) {
                this.compareButton.classList.add('tw-hidden');
            }
            if (this.compareModalButton) {
                this.compareModalButton.classList.remove('tw-hidden');
            }

            // Handle empty text visibility
            const compareProducts = this.getCompareProducts();
            const emptyText = this.buttonWrapper.querySelector('.empty__text');
            
            if (emptyText) {
                if (compareProducts.length < 1) {
                    emptyText.classList.add('active');
                } else {
                    emptyText.classList.remove('active');
                }
            }

            // Show products container if it exists
            const productsContainer = this.buttonWrapper.querySelector('.selected-products');
            if (productsContainer) {
                productsContainer.style.display = 'flex';
            }

            this.updateCompareButtonState();
            this.overlayOnProducts();
        } catch (error) {
            console.error('ProductCompare: Error opening viewer:', error);
        }
    }

    closeViewer() {
        try {
            if (!this.buttonWrapper) return;
            
            this.buttonWrapper.classList.remove('active');
            
            const closeButton = this.buttonWrapper.querySelector('.close__compare');
            if (closeButton) {
                closeButton.classList.remove('active');
            }
            
            const emptyText = this.buttonWrapper.querySelector('.empty__text');
            if (emptyText) {
                emptyText.classList.remove('active');
            }

            // Show compare button and hide modal button
            if (this.compareButton) {
                this.compareButton.classList.remove('tw-hidden');
            }
            if (this.compareModalButton) {
                this.compareModalButton.classList.add('tw-hidden');
            }

            // Hide products container
            const productsContainer = this.buttonWrapper.querySelector('.selected-products');
            if (productsContainer) {
                productsContainer.style.display = 'none';
            }

            // Remove overlay from all products
            this.removeOverlayFromAllProducts();
        } catch (error) {
            console.error('ProductCompare: Error closing viewer:', error);
        }
    }

    removeOverlayFromAllProducts() {
        try {
            const productCardsWrapper = document.querySelector(".product-grid");
            if (productCardsWrapper) {
                const productCards = productCardsWrapper.querySelectorAll("borosil-product-card");
                productCards.forEach(product => {
                    product.classList.remove("active__overlay");
                });
            }
        } catch (error) {
            console.error('ProductCompare: Error removing overlay from products:', error);
        }
    }

    updateCompareButtonState() {
        try {
            const compareProducts = this.getCompareProducts();
            const productWrapper = this.buttonWrapper?.querySelector('.compare__product__wrapper');
            
            if (!productWrapper) return;
            
            const emptyText = productWrapper.querySelector('.empty__text');
            
            // Handle empty text visibility
            if (compareProducts.length > 0) {
                // Hide empty text
                if (emptyText) {
                    emptyText.classList.remove('active');
                }

                // Create or update products container
                this.updateProductsContainer(compareProducts, productWrapper);
            } else {
                // Show empty text
                if (emptyText) {
                    emptyText.classList.add('active');
                }

                // Remove products container if it exists
                const productsContainer = productWrapper.querySelector('.selected-products');
                if (productsContainer) {
                    productsContainer.remove();
                }
            }

            // Update modal button state
            this.updateModalButtonState(compareProducts.length);
        } catch (error) {
            console.error('ProductCompare: Error updating compare button state:', error);
        }
    }

updateProductsContainer(compareProducts, productWrapper) {
        try {
            let productsContainer = productWrapper.querySelector('.selected-products');
            if (!productsContainer) {
                productsContainer = document.createElement('div');
                productsContainer.className = 'selected-products tw-flex tw-gap-[1.2rem] tw-max-w-[600px]';
                productsContainer.style.display = 'none';
                productWrapper.appendChild(productsContainer);
            }

            // Generate products HTML
            productsContainer.innerHTML = compareProducts.map(product => {
                const price = (product.price || 0) / 100;
                const compareAtPrice = (product.compare_at_price || 0) / 100;
                const discount = compareAtPrice ? Math.round((compareAtPrice - price) / compareAtPrice * 100) : 0;
                const closeButton = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M17.4001 6.6L6.6001 17.4M6.6001 6.6L17.4001 17.4" stroke="#4A0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
                return `
                    <div class="selected-product tw-flex tw-items-center tw-gap-[1.2rem] tw-bg-white tw-p-[1rem] tw-rounded">
                        <div class="remove_compare_button tw-cursor-pointer tw-hover:tw-opacity-70 tw-transition-opacity" data-product-id="${product.id}" data-product-handle="${product.handle}">
                         ${closeButton}        
                         </div>
                        <div class="product-image tw-w-[48px] tw-h-[48px] tw-max-w-[48px] tw-max-h-[48px]">
                            <img src="${product.featured_image || product.images?.[0] || ''}" 
                                 alt="${product.title || 'Product'}"
                                 class="tw-w-full tw-h-full tw-object-cover"
                                 onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjFGMUYxIi8+CjxwYXRoIGQ9Ik0yNCAzMkMyOC40MTgzIDMyIDMyIDI4LjQxODMgMzIgMjRDMzIgMTkuNTgxNyAyOC40MTgzIDE2IDI0IDE2QzE5LjU4MTcgMTYgMTYgMTkuNTgxNyAxNiAyNEMxNiAyOC40MTgzIDE5LjU4MTcgMzIgMjQgMzJaIiBzdHJva2U9IiM5QTlBOUEiIHN0cm9rZS13aWR0aD0iMiIvPgo8L3N2Zz4K';">
                        </div>
                        <div class="product-details tw-flex tw-flex-col">
                            <span class="product-title tw-whitespace-nowrap tw-text-[1.4rem] tw-font-medium tw-text-[#4A4A4A] tw-line-clamp-1">
                                 ${product.title || 'Unknown Product'}
                            </span>
                            <div class="product-price tw-flex tw-gap-[0.8rem] tw-items-center">
                                <span class="current-price tw-text-[1.2rem] tw-font-bold tw-text-[#4A4A4A]">
                                    ₹${price.toFixed(2)}
                                </span>
                                ${compareAtPrice ? `
                                    <span class="compare-price tw-text-[1rem] tw-line-through tw-text-[#9A9A9A]">
                                        ₹${compareAtPrice.toFixed(2)}
                                    </span>
                                    <span class="discount tw-text-[1rem] tw-text-[#44AA00]">
                                        ${discount}% OFF
                                    </span>
                                ` : ''}
                            </div>
                        </div>
                    </div>
                `;
            }).join('');

            // Add event listeners for remove buttons
            this.attachRemoveButtonListeners(productsContainer);
        } catch (error) {
            console.error('ProductCompare: Error updating products container:', error);
        }
    }
 // New method to attach event listeners to remove buttons
    attachRemoveButtonListeners(productsContainer) {
        try {
            const removeButtons = productsContainer.querySelectorAll('.remove_compare_button');
            removeButtons.forEach(button => {
                button.addEventListener('click', (e) => {
                    
                    e.preventDefault();
                    e.stopPropagation();
                    
                    const productId = parseInt(button.dataset.productId);
                    const productHandle = button.dataset.productHandle;
                    
                    if (productId && productHandle) {
                        this.removeProductFromComparisonByButton(productId, productHandle);
                    }
                });
            });
        } catch (error) {
            console.error('ProductCompare: Error attaching remove button listeners:', error);
        }
    }

    // New method to handle product removal from the remove button
    removeProductFromComparisonByButton(productId, productHandle) {
        try {
            const compareProducts = this.getCompareProducts();
            const productIndex = compareProducts.findIndex(p => p.id === productId);
            
            if (productIndex === -1) {
                console.warn('ProductCompare: Product not found in comparison');
                return;
            }
            
            const product = compareProducts[productIndex];
            
            // Remove from array
            compareProducts.splice(productIndex, 1);
            
            // Update session storage
            this.setCompareProducts(compareProducts);
            
            // Update UI overlay for the product card
            const productCard = document.querySelector(`[data-product-variant-handle="${productHandle}"]`);
            if (productCard) {
                this.removeProductOverlay(productCard);
            }
            
            // Update the compare button state and UI
            this.updateCompareButtonState();
            
            // Show toast notification
            this.showToast(`${product.title} removed from comparison`);
            
            console.log(`Product removed via button: ${product.title}`);
        } catch (error) {
            console.error('ProductCompare: Error removing product from comparison via button:', error);
            this.showToast('Error removing product. Please try again.');
        }
    }
    updateModalButtonState(productCount) {
        try {
            if (!this.compareModalButton) return;
            
            if (productCount <= 1) {
                this.compareModalButton.setAttribute('disabled', '');
                this.compareModalButton.style.opacity = '0.5';
                this.compareModalButton.style.background = 'grey';
            } else {
                this.compareModalButton.removeAttribute('disabled');
                this.compareModalButton.style.opacity = '1';
                this.compareModalButton.style.background = '#4A0';
            }
        } catch (error) {
            console.error('ProductCompare: Error updating modal button state:', error);
        }
    }

    overlayOnProducts() {
        try {
            const productCardsWrapper = document.querySelector(".product-grid");
            if (!productCardsWrapper) {
                console.warn('ProductCompare: Product grid not found');
                return;
            }
            const compareProducts = JSON.parse(sessionStorage.getItem('compareProducts')) || [];
            const productCards = productCardsWrapper.querySelectorAll("borosil-product-card");
            productCards.forEach(product => {
                    const productCard = product.closest('[data-product-variant-handle]');

            
            const productHandle = productCard.dataset.productVariantHandle;
              const found = compareProducts.find(x=>x.handle==productHandle)
               product.classList.add("active__overlay");
              if(!found){
                            productCard.style.setProperty('--overlay-background', '#fff');
            productCard.style.setProperty('--overlay-opacity', '0.6');
            productCard.classList.remove('product-selected');


              }
              else{
            productCard.style.setProperty('--overlay-background', 'transparent');
            productCard.style.setProperty('--overlay-opacity', '1');
            productCard.classList.add('product-selected');
              }
                
                // Remove existing event listeners to prevent duplicates
                product.removeEventListener("click", this.boundHandleProductClick);
                product.addEventListener("click", this.boundHandleProductClick);
            });
        } catch (error) {
            console.error('ProductCompare: Error adding overlay to products:', error);
        }
    }

    handleProductClick(e) {
        try {
            if(this.buttonWrapper.classList.contains('active')){
                e.preventDefault();
                this.getProductDetails(e.target);
            } 
        } catch (error) {
            console.error('ProductCompare: Error handling product click:', error);
        }
    }

    getProductDetails(target) {
        try {
            // Find the product card element
            const productCard = target.closest('[data-product-variant-handle]');
            if (!productCard) {
                console.warn('ProductCompare: Product card not found');
                return;
            }
            
            const productHandle = productCard.dataset.productVariantHandle;
            if (!productHandle) {
                console.warn('ProductCompare: Product handle not found');
                return;
            }
            
            // Check if Shopify routes are available
            if (!window.Shopify?.routes?.root) {
                console.error('ProductCompare: Shopify routes not available');
                return;
            }
            
            // Fetch product details
            fetch(`${window.Shopify.routes.root}products/${productHandle}.js`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then(product => this.addProductsToSessionStorage(product))
                .catch(error => {
                    console.error('ProductCompare: Error fetching product details:', error);
                    this.showToast('Error loading product details. Please try again.');
                });
        } catch (error) {
            console.error('ProductCompare: Error getting product details:', error);
        }
    }

    addProductsToSessionStorage(product) {
        try {
            if (!product || !product.handle) {
                console.error('ProductCompare: Invalid product data');
                return;
            }
            
            const productCard = document.querySelector(`[data-product-variant-handle="${product.handle}"]`);
            if (!productCard) {
                console.warn('ProductCompare: Product card not found for handle:', product.handle);
                return;
            }
            
            const compareProducts = this.getCompareProducts();
            const productIndex = compareProducts.findIndex(p => p.id === product.id);

            // Prevent multiple operations
            if (productCard.dataset.processing === 'true') {
                return;
            }
            productCard.dataset.processing = 'true';

            try {
                if (productIndex === -1) {
                    // Check if already 3 products in comparison
                    if (compareProducts.length >= 3) {
                        this.showToast("Maximum 3 products can be compared");
                        return;
                    }
                    
                    // Add product
                    compareProducts.push(product);
                    this.applyProductOverlay(productCard);
                    console.log(`Product added: ${product.title}`);
                    this.showToast(`${product.title} added to comparison`);
                } else {
                    // Remove product
                    compareProducts.splice(productIndex, 1);
                    this.removeProductOverlay(productCard);
                    console.log(`Product removed: ${product.title}`);
                    this.showToast(`${product.title} removed from comparison`);
                }

                // Save to session storage
                this.setCompareProducts(compareProducts);
                this.updateCompareButtonState();

                // Show products container if viewer is open
                if (this.buttonWrapper?.classList.contains('active')) {
                    const productsContainer = this.buttonWrapper.querySelector('.selected-products');
                    if (productsContainer) {
                        productsContainer.style.display = 'flex';
                    }
                }
            } finally {
                // Reset processing flag
                setTimeout(() => {
                    productCard.dataset.processing = 'false';
                }, 300);
            }
        } catch (error) {
            console.error('ProductCompare: Error adding/removing product:', error);
            this.showToast('An error occurred. Please try again.');
        }
    }

    showToast(message) {
        try {
            // Remove any existing toast
            const existingToast = document.querySelector('.toast-notification');
            if (existingToast) {
                existingToast.remove();
            }
            
            // Create toast element
            const toast = document.createElement('div');
            toast.className = 'toast-notification';
            toast.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background: #4A4A4A;
                color: white;
                padding: 12px 20px;
                border-radius: 6px;
                z-index: 9999;
                transform: translateX(100%);
                transition: transform 0.3s ease;
                max-width: 300px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            `;
            
            toast.innerHTML = `
                <div style="display: flex; align-items: center; gap: 8px;">
                    <span>${message}</span>
                </div>
                <div class="toast-timer" style="
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 2px;
                    background: #44AA00;
                    width: 100%;
                    animation: toast-timer 3s linear forwards;
                "></div>
            `;
            
            // Add CSS animation for timer
            if (!document.querySelector('#toast-style')) {
                const style = document.createElement('style');
                style.id = 'toast-style';
                style.textContent = `
                    @keyframes toast-timer {
                        from { width: 100%; }
                        to { width: 0%; }
                    }
                `;
                document.head.appendChild(style);
            }
            
            // Add to document
            document.body.appendChild(toast);
            
            // Trigger animation
            setTimeout(() => {
                toast.style.transform = 'translateX(0)';
            }, 10);
            
            // Remove toast after 3 seconds
            setTimeout(() => {
                toast.style.transform = 'translateX(100%)';
                setTimeout(() => {
                    if (toast.parentNode) {
                        toast.remove();
                    }
                }, 300);
            }, 3000);
        } catch (error) {
            console.error('ProductCompare: Error showing toast:', error);
        }
    }

    openCompareModal() {
        try {
            const compareProducts = this.getCompareProducts();
            if (compareProducts.length <= 1) {
                this.showToast('Please select at least 2 products to compare');
                return;
            }
            
            const productCardWrapper = this.querySelector('.product__cards');
            const modalViewer = this.querySelector('.modal-viewer');
            
            if (!productCardWrapper || !modalViewer) {
                console.error('ProductCompare: Modal elements not found');
                return;
            }
            
            modalViewer.classList.add('active');
            
            // Create main container for all content
            const mainContainer = document.createElement('div');
            mainContainer.className = 'tw-flex tw-flex-col tw-gap-[2rem]';

            // Products cards container
            const productsContainer = document.createElement('div');
            productsContainer.className = `
                tw-grid tw-gap-[1rem] md:tw-gap-[2rem] tw-pt-[2.4rem] tw-pb-[2rem] 
                tw-overflow-x-auto tw-snap-x tw-snap-mandatory
                md:tw-grid product-card-grid-auto
                tw-w-full
                tw-no-scrollbar
            `;
            
            // Add product cards
            compareProducts.forEach((product, index) => {
                const productCard = this.createProductCard(product, index);
                productsContainer.appendChild(productCard);
            });

            // Create comparison sections
            const comparisonSections = this.createComparisonSections(compareProducts);
            
            // Assemble all sections
            mainContainer.appendChild(productsContainer);
            mainContainer.appendChild(comparisonSections);

            productCardWrapper.innerHTML = '';
            productCardWrapper.appendChild(mainContainer);
        } catch (error) {
            console.error('ProductCompare: Error opening compare modal:', error);
            this.showToast('Error opening comparison modal. Please try again.');
        }
        
    }

    createProductCard(product, index) {
        const productCard = document.createElement('div');
        
        const price = (product.price || 0) / 100;
        const compareAtPrice = (product.compare_at_price || 0) / 100;
        const discount = compareAtPrice ? Math.round((compareAtPrice - price) / compareAtPrice * 100) : 0;
        
        productCard.innerHTML = `
            <borosil-product-card
                data-selector="product-card"
                data-section-id="compare-modal"
                class="product-card tw-relative bmd:tw-block"
                data-page="compare"
                id="${product.id}"
                data-product-variant-handle="${product.handle}"
                data-product-card="true"
            >
                <div class="tw-isolate tw-relative">
                    <div class="tw-overflow-hidden tw-bg-[#f1f1f1]">
                        ${this.createProductImageSection(product)}
                    </div>
                    ${this.createProductVariantsSection(product)}
                </div>
                ${this.createProductInfoSection(product, price, compareAtPrice, discount)}
            </borosil-product-card>
        `;
        
        return productCard;
    }

    createProductImageSection(product) {
        const images = product.images || [];
        const featuredImage = product.featured_image || images[0] || '';
        
        if (images.length > 1) {
            return `
                <carousel-component
                    id="product-image-compare-${product.id}"
                    data-desktopPerPage="1"
                    data-mobilePerPage="1"
                    data-showArrows="false"
                    data-gapbetweenslides="0rem"
                    data-gapbetweenslidesonmobile="0rem"
                    data-showArrowsOnMobile="false"
                    data-showDots="false"
                    data-showDotsOnMobile="false"
                    class="splide"
                >
                    <div class="splide__track">
                        <ul class="splide__list">
                            ${images.slice(0, 2).map(image => `
                                <li class="splide__slide">
                                    <a href="${product.url || '#'}" class="media-container media media--square">
                                        <div class="primary-image">
                                            <img 
                                                src="${image}"
                                                alt="${product.title || 'Product'}"
                                                loading="lazy"
                                                class="tw-w-full tw-h-full tw-object-cover"
                                                onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjFGMUYxIi8+CjxwYXRoIGQ9Ik0yNCAzMkMyOC40MTgzIDMyIDMyIDI4LjQxODMgMzIgMjRDMzIgMTkuNTgxNyAyOC40MTgzIDE2IDI0IDE2QzE5LjU4MTcgMTYgMTYgMTkuNTgxNyAxNiAyNEMxNiAyOC40MTgzIDE5LjU4MTcgMzIgMjQgMzJaIiBzdHJva2U9IiM5QTlBOUEiIHN0cm9rZS13aWR0aD0iMiIvPgo8L3N2Zz4K';">
                                        </div>
                                    </a>
                                </li>
                            `).join('')}
                        </ul>
                        <div class="my-slider-progress tw-bg-black-5 tw-w-full tw-h-[4px] tw-rounded-none tw-overflow-hidden tw-m-0">
                            <span class="my-slider-progress-bar tw-h-full tw-bg-black-80 tw-block"></span>
                        </div>
                    </div>
                </carousel-component>
            `;
        } else {
            return `
                <a href="${product.url || '#'}" class="media-container media media--square">
                    <div class="primary-image">
                        <img 
                            src="${featuredImage}"
                            alt="${product.title || 'Product'}"
                            loading="lazy"
                            class="tw-w-full tw-h-full tw-object-cover"
                            onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjFGMUYxIi8+CjxwYXRoIGQ9Ik0yNCAzMkMyOC40MTgzIDMyIDMyIDI4LjQxODMgMzIgMjRDMzIgMTkuNTgxNyAyOC40MTgzIDE2IDI0IDE2QzE5LjU4MTcgMTYgMTYgMTkuNTgxNyAxNiAyNEMxNiAyOC40MTgzIDE5LjU4MTcgMzIgMjQgMzJaIiBzdHJva2U9IiM5QTlBOUEiIHN0cm9rZS13aWR0aD0iMiIvPgo8L3N2Zz4K';">
                    </div>
                </a>
            `;
        }
    }

    createProductVariantsSection(product) {
        const variants = product.variants || [];
        const options = product.options || [];
        
        if (options.length > 0 && variants.length > 0) {
            return `
                <div class="product-card__variants tw-min-w-full tw-min-h-[4.5rem]">
                    <product-variants>
                        <div class="product-form__input product-form__input--dropdown">
                            <div class="product-form__select select">
                            <select
                                id="Option-compare-${product.id}"
                                class="select__select tw-relative tw-w-full tw-bg-[#f1f1f1] tw-text-[1.4rem] bmd:tw-text-[1.2rem] tw-font-primary tw-text-[#4A4A4A]"
                                name="options[${options[0].name}]"
                                ${variants.length === 1 ? 'disabled' : ''}
                            >
                                ${variants
                                .map(
                                    (variant) => `
                                    <option value="${variant.id}" ${variant.available ? '' : 'disabled'}>
                                        ${variant.title}
                                    </option>
                                    `
                                )
                                .join('')}
                            </select>
                            <div class="tw-absolute tw-w-[2.4rem] tw-h-[2.4rem] tw-right-[1.8rem] tw-bottom-[1rem]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M5.11914 6.55957L7.99914 9.43957L10.8791 6.55957" stroke="#9A9A9A" stroke-width="0.866667" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            </div>
                        </div>
                    </product-variants>
                </div>
            `;
        } else {
            return `<div class="product-card__variants tw-min-w-full tw-min-h-[4.5rem]"></div>`;
        }
    }

    createProductInfoSection(product, price, compareAtPrice, discount) {
        const variants = product.variants || [];
        const isAvailable = product.available !== false && variants.length > 0 && variants[0].available !== false;
        
        return `
            <div class="product-card__info tw-relative tw-flex tw-flex-col tw-gap-[10px] tw-pt-[1.2rem] tw-z-[0] go-behind tw-bg-[#f7f7f7] tw-py-[1rem] tw-px-[1.4rem]">
                <a href="${product.url || '#'}" class="product-card__title tw-text-[1.6rem] tw-inline-block tw-max-w-[16.2rem] tw-overflow-hidden">
                    <span class="product__card__title tw-text-[14px] bmd:tw-text-[1.4rem] tw-min-h-[4.8rem] bmd:tw-min-h-[4rem]">
                        ${product.title || 'Unknown Product'}
                    </span>
                </a>

                <div class="tw-pt-[0.4rem] tw-flex tw-gap-[0.4rem]">
                    <span class="tw-text-[#4A4A4A] tw-text-[12px] bmd:tw-text-[10px] tw-font-bold">
                        ₹${price.toFixed(2)}
                    </span>
                    ${compareAtPrice ? `
                        <span class="tw-line-through tw-text-[10px] bmd:tw-text-[10px] tw-font-normal tw-text-[#9A9A9A]">
                            ₹${compareAtPrice.toFixed(2)}
                        </span>
                        <span class="tw-text-[#44AA00] tw-text-[10px] bmd:tw-text-[10px] tw-font-normal">
                            ${discount}% OFF
                        </span>
                    ` : ''}
                </div>

                <div class="tw-flex tw-justify-end">
                    <product-form>
                        <form method="post" action="/cart/add" class="form" novalidate>
                            <input type="hidden" name="id" value="${variants[0]?.id || ''}" ${!isAvailable ? 'disabled' : ''}>
                            <button
                                type="submit"
                                name="add"
                                class="quick-add__submit product-card--btn tw-bg-transparent tw-relative ${!isAvailable ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}"
                                aria-label="Add to cart"
                                ${!isAvailable ? 'disabled' : ''}
                                data-sold-out-message="true"
                            >
                                <span class="add-cart--icon tw-z-[-1]">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 18 19" fill="none">
                                        <path d="M6.43359 8.4187V4.54165C6.79074 1.3695 10.7193 2.07445 10.7193 4.54165V8.4187" stroke="currentColor" stroke-width="0.942857"/>
                                        <path d="M2.19018 6.57592H14.9616V13.3426C14.9616 14.7654 13.8082 15.9188 12.3854 15.9188H4.76637C3.34358 15.9188 2.19018 14.7654 2.19018 13.3426V6.57592Z" stroke="currentColor" stroke-width="0.942857"/>
                                    </svg>
                                </span>
                                <span class="sold-out-message ${isAvailable ? 'hidden' : ''}">
                                    ${isAvailable ? '' : 'Sold out'}
                                </span>
                                <div class="loading-overlay__spinner tw-top-0 tw-left-[1rem] hidden">
                                    <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                                    </svg>
                                </div>
                            </button>
                        </form>
                    </product-form>
                </div>
            </div>
        `;
    }

    createComparisonSections(compareProducts) {
        const comparisonSections = document.createElement('div');
        comparisonSections.className = 'tw-flex tw-flex-col tw-gap-[2rem] tw-px-[2rem] tw-pb-[2rem]';

        // About section
        const aboutSection = this.createAboutSection(compareProducts);
        comparisonSections.appendChild(aboutSection);

        // Price section
        const priceSection = this.createPriceSection(compareProducts);
        comparisonSections.appendChild(priceSection);

        // You can add more comparison sections here (features, specifications, etc.)
        
        return comparisonSections;
    }

    createAboutSection(compareProducts) {
        const aboutSection = document.createElement('div');
        aboutSection.className = 'tw-flex tw-flex-col tw-gap-[1rem]';
        
        aboutSection.innerHTML = `
            <div class="tw-bg-[#DCDCDC] tw-mr-[-4.5rem] tw-ml-[-4.5rem] tw-py-[1rem] tw-px-[2rem] tw-text-center">
                <span class="tw-text-[1.4rem] tw-font-medium">About</span>
            </div>
            <div class="tw-grid tw-gap-[2rem] tw-overflow-x-auto md:tw-grid product-card-grid-auto tw-no-scrollbar">
                ${compareProducts.map(product => `
                    <div class="tw-min-w-[calc(83.333%)] md:tw-min-w-0">
                        <p class="tw-text-[1.4rem] tw-text-[#4A4A4A]">
                            ${product.description || 'No description available'}
                        </p>
                    </div>
                `).join('')}
            </div>
        `;
        
        return aboutSection;
    }

    createPriceSection(compareProducts) {
        const priceSection = document.createElement('div');
        priceSection.className = 'tw-flex tw-flex-col tw-gap-[1rem]';
        
        priceSection.innerHTML = `
            <div class="tw-bg-[#DCDCDC] tw-mr-[-4.5rem] tw-ml-[-4.5rem] tw-py-[1rem] tw-px-[2rem] tw-text-center">
                <span class="tw-text-[1.4rem] tw-font-medium">Price</span>
            </div>
            <div class="tw-grid tw-gap-[2rem] tw-overflow-x-auto md:tw-grid product-card-grid-auto tw-no-scrollbar">
                ${compareProducts.map(product => {
                    const price = (product.price || 0) / 100;
                    const compareAtPrice = (product.compare_at_price || 0) / 100;
                    const discount = compareAtPrice ? Math.round((compareAtPrice - price) / compareAtPrice * 100) : 0;
                    
                    return `
                        <div class="tw-min-w-[calc(83.333%)] md:tw-min-w-0 tw-flex tw-flex-col tw-gap-[0.4rem]">
                            <div class="tw-flex tw-gap-[0.8rem] tw-items-center">
                                <span class="tw-text-[1.4rem] tw-font-bold tw-text-[#4A4A4A]">
                                    ₹${price.toFixed(2)}
                                </span>
                                ${compareAtPrice ? `
                                    <span class="tw-text-[1rem] tw-line-through tw-text-[#9A9A9A]">
                                        ₹${compareAtPrice.toFixed(2)}
                                    </span>
                                    <span class="tw-text-[1rem] tw-text-[#44AA00]">
                                        ${discount}% OFF
                                    </span>
                                ` : ''}
                            </div>
                        </div>
                    `;
                }).join('')}
            </div>
        `;
        
        return priceSection;
    }

    // Cleanup method to remove event listeners and reset state
    cleanup() {
        try {
            // Remove event listeners
            if (this.compareButton) {
                this.compareButton.removeEventListener('click', this.openViewer);
            }
            if (this.compareModalButton) {
                this.compareModalButton.removeEventListener('click', this.openCompareModal);
            }
            if (this.closeButton) {
                this.closeButton.removeEventListener('click', this.closeViewer);
            }
            
            // Reset all product overlays
            this.resetAllProductOverlays();
            
            // Clear session storage
            this.clearComparisonData();
            
            console.log('ProductCompare: Cleanup completed');
        } catch (error) {
            console.error('ProductCompare: Error during cleanup:', error);
        }
    }

    // Method to get current comparison state
    getComparisonState() {
        try {
            const compareProducts = this.getCompareProducts();
            return {
                productCount: compareProducts.length,
                products: compareProducts.map(p => ({
                    id: p.id,
                    handle: p.handle,
                    title: p.title,
                    price: p.price
                })),
                isViewerOpen: this.buttonWrapper?.classList.contains('active') || false,
                isModalOpen: this.querySelector('.modal-viewer')?.classList.contains('active') || false
            };
        } catch (error) {
            console.error('ProductCompare: Error getting comparison state:', error);
            return {
                productCount: 0,
                products: [],
                isViewerOpen: false,
                isModalOpen: false
            };
        }
    }

    // Method to programmatically add a product to comparison
    addProductToComparison(productData) {
        try {
            if (!productData || !productData.id) {
                console.error('ProductCompare: Invalid product data provided');
                return false;
            }
            
            const compareProducts = this.getCompareProducts();
            
            // Check if product already exists
            if (compareProducts.some(p => p.id === productData.id)) {
                console.warn('ProductCompare: Product already in comparison');
                return false;
            }
            
            // Check limit
            if (compareProducts.length >= 3) {
                this.showToast("Maximum 3 products can be compared");
                return false;
            }
            
            // Add product
            compareProducts.push(productData);
            this.setCompareProducts(compareProducts);
            
            // Update UI
            const productCard = document.querySelector(`[data-product-variant-handle="${productData.handle}"]`);
            if (productCard) {
                this.applyProductOverlay(productCard);
            }
            
            this.updateCompareButtonState();
            this.showToast(`${productData.title} added to comparison`);
            
            return true;
        } catch (error) {
            console.error('ProductCompare: Error adding product to comparison:', error);
            return false;
        }
    }

    // Method to programmatically remove a product from comparison
    removeProductFromComparison(productId) {
        try {
            const compareProducts = this.getCompareProducts();
            const productIndex = compareProducts.findIndex(p => p.id === productId);
            
            if (productIndex === -1) {
                console.warn('ProductCompare: Product not found in comparison');
                return false;
            }
            
            const product = compareProducts[productIndex];
            compareProducts.splice(productIndex, 1);
            this.setCompareProducts(compareProducts);
            
            // Update UI
            const productCard = document.querySelector(`[data-product-variant-handle="${product.handle}"]`);
            if (productCard) {
                this.removeProductOverlay(productCard);
            }
            
            this.updateCompareButtonState();
            this.showToast(`${product.title} removed from comparison`);
            
            return true;
        } catch (error) {
            console.error('ProductCompare: Error removing product from comparison:', error);
            return false;
        }
    }
}

// Register the custom element
customElements.define('product-compare', ProductCompare);